import { useState } from 'react'
import { useConfig, useMissingPlannings, useRebuildPlannings } from '@/api/queries'
import type { MissingPlanning } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton, LinkButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { dateTime } from '@/lib/format'
import { links } from '@/lib/links'

const kindTone = { Automatic: 'info', OnDemand: 'warn', Incremental: 'neutral' } as const

export function MissingPlanningsDialog({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useMissingPlannings()
  const { data: config } = useConfig()
  const rebuild = useRebuildPlannings()
  const toast = useToast()
  const [confirmAll, setConfirmAll] = useState(false)

  const run = async (plannings: MissingPlanning[]) => {
    try {
      const results = await rebuild.mutateAsync(plannings)
      const failed = results.filter((r) => !r.success)
      if (failed.length === 0) toast.notify(`Rebuild requested for ${results.length} planning${results.length === 1 ? '' : 's'}`, 'success')
      else toast.notify(`${failed.length} of ${results.length} rebuilds failed:\n${failed.map((f) => `${f.hotelId}: ${f.error}`).join('\n')}`, 'error')
    } catch (err) {
      toast.error(err, 'Rebuild failed')
    }
  }

  const columns: Column<MissingPlanning>[] = [
    {
      key: 'synchronizationKind',
      header: 'Type',
      render: (p) => (
        <Badge tone={kindTone[p.synchronizationKind]} title={p.synchronizationType}>
          {p.synchronizationType}
        </Badge>
      ),
    },
    {
      key: 'hotelId',
      header: 'Hotel',
      render: (p) => {
        const href = links.hotelSynchronizations(config?.links, p.hotelId)
        return href ? <a href={href} target="_blank" rel="noreferrer">{p.hotelId}</a> : p.hotelId
      },
    },
    { key: 'startDate', header: 'Start date', render: (p) => dateTime(p.startDate, 'yyyy-MM-dd') },
    { key: 'numberOfDays', header: 'Days', align: 'right' },
    { key: 'hours', header: 'Waiting', align: 'right', render: (p) => `${p.hours}h` },
    {
      key: 'rateScreenerSynchronizationId',
      header: 'Synchronisation',
      render: (p) => {
        const href = links.grafanaSynchronization(config?.links, p.rateScreenerSynchronizationId, p.hotelId)
        const short = p.rateScreenerSynchronizationId.slice(0, 8)
        return href ? <a href={href} target="_blank" rel="noreferrer" title="Open in Grafana">{short}…</a> : `${short}…`
      },
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: '48px',
      render: (p) => (
        <IconButton label="Rebuild this planning" onClick={() => void run([p])} disabled={rebuild.isPending}>
          <Icons.Sync size={15} />
        </IconButton>
      ),
    },
  ]

  return (
    <Modal
      title={`Missing plannings${data ? ` (${data.length})` : ''}`}
      onClose={onClose}
      size="xl"
      headerExtra={config?.links.grafanaMissingPlannings ? <LinkButton size="sm" href={config.links.grafanaMissingPlannings} icon={<Icons.External size={13} />}>Grafana</LinkButton> : undefined}
      footer={
        <Button variant="primary" onClick={() => setConfirmAll(true)} disabled={!data || data.length === 0} loading={rebuild.isPending}>
          Rebuild all missing
        </Button>
      }
    >
      <DataTable columns={columns} rows={data} rowKey={(p) => p.synchronizationId} loading={isLoading} error={error} searchable searchPlaceholder="Search by hotel id or kind" dense emptyMessage="No missing plannings" />
      {confirmAll && data && (
        <ConfirmDialog
          title="Rebuild all missing plannings"
          message={`Ask the workers to rebuild ${data.length} planning${data.length === 1 ? '' : 's'}?`}
          confirmLabel="Rebuild"
          onCancel={() => setConfirmAll(false)}
          onConfirm={() => {
            setConfirmAll(false)
            void run(data)
          }}
        />
      )}
    </Modal>
  )
}
